import { useEffect, useState } from "react";
import { withNamespaces } from "react-i18next";
import {
    Button,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import useCreateEndpoint from "../hooks/useCreateEndpoint";
import {
    getEndpointFieldLabels,
    mapEndpointToCounterpart,
} from "../helpers/flowForm";

const EMPTY_FORM = {
    name: "",
    identifier: "",
};

const FieldError = ({ error }) => {
    if (!error) {
        return null;
    }

    return <div className="invalid-feedback d-block">{error}</div>;
};

const CreateEndpointModal = ({
    isOpen,
    type,
    typeLabel,
    onClose,
    onCreated,
    t,
}) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const { createEndpoint, isCreating } = useCreateEndpoint({
        t,
        onError: setErrors,
        onSuccess: (endpoint) => {
            if (typeof onCreated === "function") {
                onCreated(mapEndpointToCounterpart(endpoint));
            }

            onClose();
        },
    });

    useEffect(() => {
        if (!isOpen) {
            setForm(EMPTY_FORM);
            setErrors({});
        }
    }, [isOpen]);

    const handleClose = () => {
        if (isCreating) {
            return;
        }

        onClose();
    };

    const fieldLabels = getEndpointFieldLabels(type);

    const handleSubmit = () => {
        const name = form.name.trim();

        if (!name) {
            setErrors({
                name: t("Endpoint name is required"),
            });
            return;
        }

        createEndpoint({
            type,
            name,
            identifier: form.identifier.trim() || null,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            toggle={handleClose}
            centered
            zIndex={2000}
        >
            <ModalHeader toggle={isCreating ? undefined : handleClose}>
                {t("Create new {{type}}", { type: typeLabel })}
            </ModalHeader>
            <ModalBody>
                <div className="mb-3">
                    <Label className="form-label" for="data-flow-v2-endpoint-name">
                        {t(fieldLabels.name)}
                        {fieldLabels.nameRequired ? (
                            <span className="text-danger"> *</span>
                        ) : null}
                    </Label>
                    <Input
                        id="data-flow-v2-endpoint-name"
                        type="text"
                        value={form.name}
                        onChange={(event) =>
                            setForm((current) => ({
                                ...current,
                                name: event.target.value,
                            }))
                        }
                        disabled={isCreating}
                    />
                    <FieldError error={errors.name || errors.endpoint} />
                </div>

                <div>
                    <Label
                        className="form-label"
                        for="data-flow-v2-endpoint-identifier"
                    >
                        {t(fieldLabels.identifier)}
                        {fieldLabels.identifierOptional
                            ? ` ${t("(optional)")}`
                            : null}
                    </Label>
                    <Input
                        id="data-flow-v2-endpoint-identifier"
                        type="text"
                        value={form.identifier}
                        onChange={(event) =>
                            setForm((current) => ({
                                ...current,
                                identifier: event.target.value,
                            }))
                        }
                        disabled={isCreating}
                    />
                    <FieldError error={errors.identifier} />
                </div>
            </ModalBody>
            <ModalFooter>
                <Button
                    color="secondary"
                    outline
                    type="button"
                    onClick={handleClose}
                    disabled={isCreating}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    color="primary"
                    type="button"
                    onClick={handleSubmit}
                    disabled={isCreating}
                >
                    {isCreating ? <Spinner size="sm" /> : t("Create")}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default withNamespaces()(CreateEndpointModal);
